import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface SectionData {
  name: string;
  icon: string;
  order: number;
  views: number;
  uniqueVisitors: number;
  avgTimeSpent: number | null;
  buttonClicks: number;
  convertedFromHere: number;
  dropoffAfter: number;
}

const SECTION_CONFIG: { key: string; name: string; icon: string; order: number }[] = [
  { key: 'Hero',           name: 'Hero (Giriş)',       icon: 'ri-home-4-line',          order: 1 },
  { key: 'Problem',        name: 'Problem',             icon: 'ri-error-warning-line',   order: 2 },
  { key: 'Çözüm',          name: 'Çözüm',               icon: 'ri-lightbulb-line',        order: 3 },
  { key: 'Nasıl Çalışır',  name: 'Nasıl Çalışır',       icon: 'ri-settings-3-line',       order: 4 },
  { key: 'Özellikler',     name: 'Özellikler',          icon: 'ri-star-line',             order: 5 },
  { key: 'Paketler',       name: 'Paketler / Fiyatlar', icon: 'ri-price-tag-3-line',      order: 6 },
  { key: 'Yorumlar',       name: 'Müşteri Yorumları',   icon: 'ri-chat-quote-line',       order: 7 },
  { key: 'SSS',            name: 'Sık Sorulan Sorular', icon: 'ri-question-answer-line',  order: 8 },
  { key: 'CTA',            name: 'CTA (Son Çağrı)',     icon: 'ri-megaphone-line',        order: 9 },
];

function getHeatColor(ratio: number): { bg: string; bar: string; text: string } {
  if (ratio >= 0.85) return { bg: 'rgba(240,180,41,0.18)', bar: '#f0b429', text: '#f0b429' };
  if (ratio >= 0.65) return { bg: 'rgba(251,146,60,0.14)', bar: '#fb923c', text: '#fb923c' };
  if (ratio >= 0.45) return { bg: 'rgba(0,212,255,0.10)', bar: '#00d4ff', text: '#00d4ff' };
  if (ratio >= 0.25) return { bg: 'rgba(148,163,184,0.08)', bar: '#64748b', text: '#64748b' };
  return { bg: 'rgba(248,113,113,0.08)', bar: '#f87171', text: '#f87171' };
}

function getHeatLabel(ratio: number): string {
  if (ratio >= 0.85) return 'Çok Yoğun';
  if (ratio >= 0.65) return 'Yoğun';
  if (ratio >= 0.45) return 'Orta';
  if (ratio >= 0.25) return 'Seyrek';
  return 'Neredeyse Yok';
}

export default function SectionHeatmap() {
  const [sections, setSections] = useState<SectionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSessions, setTotalSessions] = useState(0);
  const [viewMode, setViewMode] = useState<'heatmap' | 'table'>('heatmap');

  const fetchData = useCallback(async () => {
    const { data } = await supabase
      .from('page_events')
      .select('session_id, event_type, section, event_label, extra, created_at');

    if (!data) { setLoading(false); return; }

    type RawEvent = {
      session_id: string;
      event_type: string;
      section: string | null;
      event_label: string | null;
      extra: Record<string, unknown> | null;
      created_at: string;
    };

    const events = data as RawEvent[];

    // Count unique sessions
    const allSessions = new Set(events.map((e) => e.session_id));
    setTotalSessions(allSessions.size);

    // Build section stats
    const sectionMap = new Map<string, {
      views: number;
      uniqueVisitors: Set<string>;
      buttonClicks: number;
      convertedFromHere: number;
    }>();

    SECTION_CONFIG.forEach((s) => {
      sectionMap.set(s.key, { views: 0, uniqueVisitors: new Set(), buttonClicks: 0, convertedFromHere: 0 });
    });

    // Track which section each session last interacted with before converting
    const sessionLastSection = new Map<string, string>();
    const sessionConverted = new Set<string>();

    events.forEach((ev) => {
      if (ev.event_type === 'section_view' && ev.section) {
        const s = sectionMap.get(ev.section);
        if (s) {
          s.views += 1;
          s.uniqueVisitors.add(ev.session_id);
          sessionLastSection.set(ev.session_id, ev.section);
        }
      }
      if (ev.event_type === 'button_click' && ev.section) {
        const s = sectionMap.get(ev.section);
        if (s) s.buttonClicks += 1;
      }
      if (ev.event_type === 'whatsapp_submit') {
        sessionConverted.add(ev.session_id);
      }
    });

    // Count conversions attributed to last section
    sessionConverted.forEach((sid) => {
      const lastSec = sessionLastSection.get(sid);
      if (lastSec) {
        const s = sectionMap.get(lastSec);
        if (s) s.convertedFromHere += 1;
      }
    });

    // Calculate dropoff: sessions that viewed this section but NOT the next
    const result: SectionData[] = SECTION_CONFIG.map((cfg, idx) => {
      const s = sectionMap.get(cfg.key) ?? { views: 0, uniqueVisitors: new Set<string>(), buttonClicks: 0, convertedFromHere: 0 };
      const nextCfg = SECTION_CONFIG[idx + 1];
      let dropoffAfter = 0;
      if (nextCfg) {
        const nextS = sectionMap.get(nextCfg.key);
        const thisUnique = s.uniqueVisitors.size;
        const nextUnique = nextS?.uniqueVisitors.size ?? 0;
        dropoffAfter = Math.max(0, thisUnique - nextUnique);
      }

      return {
        name: cfg.name,
        icon: cfg.icon,
        order: cfg.order,
        views: s.views,
        uniqueVisitors: s.uniqueVisitors.size,
        avgTimeSpent: null,
        buttonClicks: s.buttonClicks,
        convertedFromHere: s.convertedFromHere,
        dropoffAfter,
      };
    });

    setSections(result);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const maxViews = Math.max(...sections.map((s) => s.uniqueVisitors), 1);

  return (
    <div
      className="rounded-2xl p-5"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h3 className="text-white font-bold text-sm">Bölüm Isı Haritası</h3>
          <p className="text-gray-500 text-xs mt-0.5">
            Hangi bölüm ne kadar görüntülendi, nerede kayıp var
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div
            className="flex items-center gap-1 p-1 rounded-lg"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            {([
              { key: 'heatmap', icon: 'ri-fire-line', label: 'Isı' },
              { key: 'table',   icon: 'ri-table-line', label: 'Tablo' },
            ] as { key: 'heatmap' | 'table'; icon: string; label: string }[]).map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setViewMode(m.key)}
                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap"
                style={{
                  background: viewMode === m.key ? 'rgba(240,180,41,0.2)' : 'transparent',
                  color: viewMode === m.key ? '#f0b429' : '#6b7280',
                }}
              >
                <i className={`${m.icon} text-[10px]`} />
                {m.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => { setLoading(true); fetchData(); }}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white transition-colors cursor-pointer"
            style={{ background: 'rgba(255,255,255,0.05)' }}
            title="Yenile"
          >
            <i className={`ri-refresh-line text-xs ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line text-2xl animate-spin" style={{ color: '#f0b429' }} />
        </div>
      ) : viewMode === 'heatmap' ? (
        /* ── HEATMAP VIEW ── */
        <div className="space-y-2">
          {sections.map((sec, idx) => {
            const ratio = totalSessions > 0 ? sec.uniqueVisitors / totalSessions : 0;
            const heat = getHeatColor(ratio);
            const pct = Math.round(ratio * 100);
            const isTop = sec.uniqueVisitors === maxViews;

            return (
              <div key={sec.name}>
                <div
                  className="relative flex items-center gap-3 px-4 py-3 rounded-xl overflow-hidden transition-all"
                  style={{ background: heat.bg, border: `1px solid ${heat.bar}20` }}
                >
                  {/* Heat fill bar (background) */}
                  <div
                    className="absolute inset-y-0 left-0 rounded-xl transition-all duration-700"
                    style={{
                      width: `${pct}%`,
                      background: `linear-gradient(90deg, ${heat.bar}18, ${heat.bar}06)`,
                      pointerEvents: 'none',
                    }}
                  />

                  {/* Order badge */}
                  <div
                    className="w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 z-10 text-[10px] font-black"
                    style={{ background: `${heat.bar}20`, color: heat.bar }}
                  >
                    {idx + 1}
                  </div>

                  {/* Section icon */}
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0 z-10"
                    style={{ background: `${heat.bar}15`, border: `1px solid ${heat.bar}25` }}
                  >
                    <i className={`${sec.icon} text-sm`} style={{ color: heat.bar }} />
                  </div>

                  {/* Name + bar */}
                  <div className="flex-1 min-w-0 z-10">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white text-xs font-semibold">{sec.name}</span>
                      {isTop && (
                        <span
                          className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                          style={{ background: 'rgba(240,180,41,0.2)', color: '#f0b429' }}
                        >
                          EN POPÜLER
                        </span>
                      )}
                      {sec.uniqueVisitors === 0 && (
                        <span
                          className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                          style={{ background: 'rgba(248,113,113,0.15)', color: '#f87171' }}
                        >
                          HİÇ GÖRÜLMEDİ
                        </span>
                      )}
                    </div>
                    {/* Progress bar */}
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${pct}%`, background: heat.bar }}
                      />
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 flex-shrink-0 z-10">
                    <div className="text-right hidden sm:block">
                      <p className="text-white text-sm font-black">{sec.uniqueVisitors}</p>
                      <p className="text-gray-600 text-[10px]">ziyaretçi</p>
                    </div>
                    <div className="text-right hidden md:block">
                      <p className="font-bold text-sm" style={{ color: heat.bar }}>%{pct}</p>
                      <p className="text-gray-600 text-[10px]">{getHeatLabel(ratio)}</p>
                    </div>
                  </div>
                </div>

                {/* Dropoff arrow between sections */}
                {idx < sections.length - 1 && sec.dropoffAfter > 0 && (
                  <div className="flex items-center gap-2 ml-[72px] my-0.5">
                    <i className="ri-arrow-down-line text-[10px] text-gray-700" />
                    <span className="text-[10px] text-gray-700">
                      {sec.dropoffAfter} kişi bir sonraki bölüme geçmedi
                    </span>
                    {sec.dropoffAfter > 2 && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(248,113,113,0.1)', color: '#f87171' }}>
                        kayıp noktası
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Legend */}
          <div className="flex items-center gap-3 pt-3 flex-wrap">
            <span className="text-gray-600 text-[10px] font-semibold uppercase tracking-wider">Yoğunluk:</span>
            {[
              { label: 'Çok Yoğun', color: '#f0b429' },
              { label: 'Yoğun', color: '#fb923c' },
              { label: 'Orta', color: '#00d4ff' },
              { label: 'Seyrek', color: '#64748b' },
              { label: 'Yok', color: '#f87171' },
            ].map((l) => (
              <div key={l.label} className="flex items-center gap-1">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: l.color }} />
                <span className="text-gray-600 text-[10px]">{l.label}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* ── TABLE VIEW ── */
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {['#', 'Bölüm', 'Görüntülenme', 'Benzersiz', 'Oran', 'Buton Tıklaması', 'Dönüşüm', 'Sonraki Bölüme Geçmedi'].map((h) => (
                  <th key={h} className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sections.map((sec, idx) => {
                const ratio = totalSessions > 0 ? sec.uniqueVisitors / totalSessions : 0;
                const heat = getHeatColor(ratio);
                return (
                  <tr
                    key={sec.name}
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                  >
                    <td className="px-3 py-2.5">
                      <span className="text-gray-600 text-xs">{idx + 1}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <i className={`${sec.icon} text-xs`} style={{ color: heat.bar }} />
                        <span className="text-white text-xs font-semibold whitespace-nowrap">{sec.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-gray-300 text-xs">{sec.views}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-white text-xs font-bold">{sec.uniqueVisitors}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-xs font-bold" style={{ color: heat.bar }}>
                        %{Math.round(ratio * 100)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-gray-300 text-xs">{sec.buttonClicks || '—'}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      {sec.convertedFromHere > 0 ? (
                        <span className="text-xs font-bold text-green-400">{sec.convertedFromHere}</span>
                      ) : (
                        <span className="text-gray-700 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {sec.dropoffAfter > 0 ? (
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded-md"
                          style={{
                            background: sec.dropoffAfter > 2 ? 'rgba(248,113,113,0.1)' : 'rgba(255,255,255,0.04)',
                            color: sec.dropoffAfter > 2 ? '#f87171' : '#6b7280',
                          }}
                        >
                          -{sec.dropoffAfter}
                        </span>
                      ) : (
                        <span className="text-gray-700 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary insight */}
      {!loading && sections.length > 0 && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {(() => {
            const topSec = [...sections].sort((a, b) => b.uniqueVisitors - a.uniqueVisitors)[0];
            const deadSec = sections.filter((s) => s.uniqueVisitors === 0);
            const bigDropSec = sections.slice(0, -1).sort((a, b) => b.dropoffAfter - a.dropoffAfter)[0];
            return [
              {
                icon: 'ri-fire-line',
                color: '#f0b429',
                bg: 'rgba(240,180,41,0.08)',
                border: 'rgba(240,180,41,0.2)',
                title: 'En Çok Görüntülenen',
                value: topSec?.name ?? '—',
                sub: `${topSec?.uniqueVisitors ?? 0} benzersiz ziyaretçi`,
              },
              {
                icon: 'ri-arrow-down-line',
                color: '#f87171',
                bg: 'rgba(248,113,113,0.08)',
                border: 'rgba(248,113,113,0.2)',
                title: 'En Büyük Kayıp Noktası',
                value: bigDropSec?.name ?? '—',
                sub: `${bigDropSec?.dropoffAfter ?? 0} kişi sonraki bölüme geçmedi`,
              },
              {
                icon: 'ri-eye-off-line',
                color: '#64748b',
                bg: 'rgba(100,116,139,0.08)',
                border: 'rgba(100,116,139,0.2)',
                title: 'Hiç Görülmeyen Bölüm',
                value: deadSec.length > 0 ? deadSec.map((s) => s.name).join(', ') : 'Yok — Harika!',
                sub: deadSec.length > 0 ? `${deadSec.length} bölüm hiç görülmedi` : 'Tüm bölümler görüntülendi',
              },
            ];
          })().map((card) => (
            <div
              key={card.title}
              className="rounded-xl p-3"
              style={{ background: card.bg, border: `1px solid ${card.border}` }}
            >
              <div className="flex items-center gap-1.5 mb-1.5">
                <i className={`${card.icon} text-xs`} style={{ color: card.color }} />
                <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: card.color }}>
                  {card.title}
                </span>
              </div>
              <p className="text-white text-xs font-bold leading-snug">{card.value}</p>
              <p className="text-gray-600 text-[10px] mt-0.5">{card.sub}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
